import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import proxy from "../../proxy";

import ltcQR from "../../assets/ltc.png";
import bchQR from "../../assets/bch.png";
import dogeQR from "../../assets/doge.png";
import dashQR from "../../assets/dash.png";
import polQR from "../../assets/pol.png";
import zecQR from "../../assets/zec.png";

import ltcLogo from "../../assets/ltc_icon.png";
import bchLogo from "../../assets/bch_icon.png";
import dogeLogo from "../../assets/doge_icon.png";
import dashLogo from "../../assets/dash_logo.png";
import polLogo from "../../assets/pol_logo.png";
import zecLogo from "../../assets/zec_logo.png";

import {
  Box,
  Text,
  Image,
  Button,
  Link,
  VStack,
  HStack,
  Badge,
  Tooltip,
  useToast,
  useBreakpointValue,
  Skeleton,
  SkeletonText,
  IconButton,
  Collapse,
} from "@chakra-ui/react";
import { ExternalLinkIcon, CopyIcon, CheckIcon, ChevronDownIcon, ChevronUpIcon } from "@chakra-ui/icons";

/* -------------------------
   MAP-URI LOGO / QR / CULORI
-------------------------- */
const QR = {
  ltc: ltcQR,
  bch: bchQR,
  doge: dogeQR,
  dash: dashQR,
  pol: polQR,
  zec: zecQR,
};

const LOGO = {
  ltclogo: ltcLogo,
  bchlogo: bchLogo,
  dogelogo: dogeLogo,
  dashlogo: dashLogo,
  pollogo: polLogo,
  zeclogo: zecLogo,
};

const COLORS = {
  LTC: { color: "#345D9D", gradient: "linear(to-br, #345D9D, #5A95D8)", shadow: "rgba(52, 93, 157, 0.4)" },
  BCH: { color: "#8DC351", gradient: "linear(to-br, #8DC351, #B8E986)", shadow: "rgba(141, 195, 81, 0.4)" },
  DOGE: { color: "#C3A634", gradient: "linear(to-br, #C3A634, #F0D365)", shadow: "rgba(195, 166, 52, 0.4)" },
  DASH: { color: "#008DE4", gradient: "linear(to-br, #008DE4, #40B5F5)", shadow: "rgba(0, 141, 228, 0.4)" },
  POL: { color: "#8247E5", gradient: "linear(to-br, #8247E5, #A87EF0)", shadow: "rgba(130, 71, 229, 0.4)" },
  ZEC: { color: "#ECB244", gradient: "linear(to-br, #ECB244, #F5D078)", shadow: "rgba(236, 178, 68, 0.4)" },
  DEFAULT: { color: "#38B2AC", gradient: "linear(to-br, #38B2AC, #4FD1C5)", shadow: "rgba(56, 178, 172, 0.4)" },
};

/* -------------------------
   HOOK FETCH WALLET API
-------------------------- */
const useWallet = (address) => {
  const [wallet, setWallet] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!address) return;
    axios
      .post(`${proxy}server_mts/GetWallet.php`, { address })
      .then((res) => {
        if (res.data?.length) setWallet(res.data[0]);
        else throw new Error("Invalid response");
      })
      .catch(() => setError("Unable to retrieve balance."))
      .finally(() => setLoading(false));
  }, [address]);

  return { wallet, loading, error };
};

/* ---------------------------------
   COMPONENTA OPTIMIZATĂ WALLET CARD
----------------------------------- */
export default function WalletCard({ address, qrCode, logo }) {
  const { wallet, loading, error } = useWallet(address);
  const toast = useToast();
  const cardRef = useRef(null);
  const [copied, setCopied] = useState(false);
  const [balanceRevealed, setBalanceRevealed] = useState(false);
  const [showDetails, setShowDetails] = useState(false);

  // Responsive values
  const isMobile = useBreakpointValue({ base: true, md: false });
  const cardMaxW = useBreakpointValue({ base: "full", md: "320px", lg: "340px" });
  const cardPadding = useBreakpointValue({ base: 3, md: 5 });
  const logoSize = useBreakpointValue({ base: "36px", md: "48px" });

  const color = wallet?.symbol && COLORS[wallet.symbol] ? COLORS[wallet.symbol] : COLORS.DEFAULT;
  const qrPath = QR[qrCode];
  const logoPath = LOGO[logo];

  const amount = wallet ? Number(wallet.amount || 0) : 0;
  const price = wallet ? Number(wallet.price || 0) : 0;
  const totalUsd = amount * price;

  // Reveal balance animation when data loads
  useEffect(() => {
    if (!loading && wallet) {
      setTimeout(() => setBalanceRevealed(true), 200);
    }
  }, [loading, wallet]);

  /* -------------------------
     3D HOVER - DESKTOP ONLY
  -------------------------- */
  const [tilt, setTilt] = useState({ x: 0, y: 0 });
  const raf = useRef(null);

  const handleMouseMove = (e) => {
    if (isMobile || !cardRef.current) return;
    cancelAnimationFrame(raf.current);
    raf.current = requestAnimationFrame(() => {
      const rect = cardRef.current.getBoundingClientRect();
      const x = ((e.clientX - rect.left) / rect.width - 0.5) * 10;
      const y = ((e.clientY - rect.top) / rect.height - 0.5) * -10;
      setTilt({ x, y });
    });
  };

  /* -------------------------
     COPY ADDRESS
  -------------------------- */
  const copy = () => {
    navigator.clipboard.writeText(address).then(() => {
      setCopied(true);
      toast({
        title: "Copied!",
        status: "success",
        position: "top",
        duration: 1500,
      });
      setTimeout(() => setCopied(false), 1500);
    });
  };

  const truncate = (a) => (a ? `${a.slice(0, 6)}...${a.slice(-6)}` : "");

  const explorer = wallet?.explorer
    ? `https://blockchair.com/${wallet.explorer}/address/${address}`
    : "#";

  /* -------------------------
     LOADING SKELETON
  -------------------------- */
  if (loading) {
    return (
      <Box
        borderRadius={{ base: "lg", md: "2xl" }}
        p={cardPadding}
        bg="linear-gradient(135deg, rgba(26,32,44,0.95), rgba(45,55,72,0.95))"
        border="2px solid"
        borderColor="whiteAlpha.200"
        maxW={cardMaxW}
        mx="auto"
      >
        <VStack spacing={2}>
          <HStack w="full" justify="space-between">
            <HStack spacing={2}>
              <Skeleton height={logoSize} width={logoSize} borderRadius="lg" />
              <VStack spacing={1} align="start">
                <Skeleton height="16px" width="50px" />
                <Skeleton height="10px" width="40px" />
              </VStack>
            </HStack>
            <Skeleton height="20px" width="50px" borderRadius="full" />
          </HStack>
          <Skeleton height="60px" width="full" borderRadius="lg" />
        </VStack>
      </Box>
    );
  }

  /* -------------------------
     ERROR STATE
  -------------------------- */
  if (error) {
    return (
      <Box
        borderRadius={{ base: "lg", md: "2xl" }}
        p={cardPadding}
        bg="linear-gradient(135deg, rgba(26,32,44,0.95), rgba(45,55,72,0.95))"
        border="2px solid"
        borderColor="red.500"
        maxW={cardMaxW}
        mx="auto"
      >
        <VStack spacing={2} py={4}>
          <Text fontSize="2xl">⚠️</Text>
          <Text color="red.300" fontSize="xs" textAlign="center" fontWeight="semibold">
            {error}
          </Text>
        </VStack>
      </Box>
    );
  }

  /* -------------------------
     MAIN RENDER - COMPACT MOBILE / FULL DESKTOP
  -------------------------- */
  return (
    <Box
      ref={cardRef}
      onMouseMove={handleMouseMove}
      onMouseLeave={() => !isMobile && setTilt({ x: 0, y: 0 })}
      style={{
        transform: isMobile ? "none" : `perspective(1000px) rotateX(${tilt.y}deg) rotateY(${tilt.x}deg)`,
      }}
      transition="transform 0.15s ease-out"
      position="relative"
      borderRadius={{ base: "lg", md: "2xl" }}
      p={cardPadding}
      bg="linear-gradient(135deg, rgba(26,32,44,0.98), rgba(45,55,72,0.98))"
      border="2px solid"
      borderColor="whiteAlpha.200"
      boxShadow={`0 ${isMobile ? '8' : '15'}px ${isMobile ? '20' : '40'}px rgba(0,0,0,0.5), 0 0 ${isMobile ? '15' : '30'}px ${color.shadow}`}
      maxW={cardMaxW}
      mx="auto"
      overflow="hidden"
      _hover={{
        borderColor: "whiteAlpha.300",
        boxShadow: `0 ${isMobile ? '10' : '20'}px ${isMobile ? '25' : '50'}px rgba(0,0,0,0.6), 0 0 ${isMobile ? '20' : '40'}px ${color.shadow}`,
      }}
    >
      {/* Animated gradient border */}
      <Box
        position="absolute"
        top={0}
        left={0}
        right={0}
        height="2px"
        bgGradient={color.gradient}
        sx={{
          animation: 'shimmer 3s ease-in-out infinite',
          '@keyframes shimmer': {
            '0%, 100%': { opacity: 0.6 },
            '50%': { opacity: 1 }
          }
        }}
      />

      {/* Subtle glow */}
      {!isMobile && (
        <Box
          position="absolute"
          inset="-2px"
          borderRadius="2xl"
          bgGradient={color.gradient}
          opacity={0.08}
          filter="blur(20px)"
          zIndex={0}
          pointerEvents="none"
        />
      )}

      <VStack spacing={{ base: 2, md: 4 }} position="relative" zIndex={2}>
        {/* COMPACT HEADER */}
        <HStack w="full" justify="space-between">
          <HStack spacing={{ base: 2, md: 3 }}>
            <Box
              bgGradient={color.gradient}
              boxSize={logoSize}
              borderRadius="lg"
              display="flex"
              alignItems="center"
              justifyContent="center"
              boxShadow={`0 0 ${isMobile ? '12' : '20'}px ${color.shadow}`}
            >
              <Image src={logoPath} alt="logo" boxSize="70%" />
            </Box>

            <VStack spacing={0} align="start">
              <Text fontSize={{ base: "md", md: "xl" }} fontWeight="bold" color="white">
                {wallet?.symbol || "..."}
              </Text>
              <Text fontSize="2xs" color="whiteAlpha.500" fontWeight="semibold" letterSpacing="wide">
                WALLET
              </Text>
            </VStack>
          </HStack>

          {/* LIVE Badge - Hidden on mobile to save space */}
          {!isMobile && (
            <Badge
              bgGradient="linear(to-r, green.400, green.500)"
              color="white"
              borderRadius="full"
              px={2}
              py={0.5}
              fontSize="2xs"
              fontWeight="bold"
              boxShadow="0 0 10px rgba(72, 187, 120, 0.4)"
            >
              ● LIVE
            </Badge>
          )}
        </HStack>

        {/* COMPACT BALANCE - Always visible */}
        <Box
          bg="whiteAlpha.100"
          px={{ base: 3, md: 4 }}
          py={{ base: 2, md: 3 }}
          borderRadius="lg"
          w="full"
          border="1px solid"
          borderColor="whiteAlpha.200"
          backdropFilter="blur(10px)"
          opacity={balanceRevealed ? 1 : 0}
          transform={balanceRevealed ? "translateY(0)" : "translateY(10px)"}
          transition="all 0.5s cubic-bezier(0.34, 1.56, 0.64, 1)"
        >
          <HStack justify="space-between" align="center">
            <VStack align="start" spacing={0}>
              <Text fontSize="2xs" color="whiteAlpha.500" fontWeight="bold" letterSpacing="wide">
                BALANCE
              </Text>
              <HStack spacing={1}>
                <Text fontSize={{ base: "lg", md: "xl" }} fontWeight="black" color="white">
                  {amount.toFixed(4)}
                </Text>
                <Text fontSize={{ base: "xs", md: "sm" }} color="whiteAlpha.600" fontWeight="semibold">
                  {wallet?.symbol}
                </Text>
              </HStack>
            </VStack>

            <VStack align="end" spacing={0}>
              <Text fontSize={{ base: "lg", md: "xl" }} fontWeight="bold" color="green.300">
                ${totalUsd.toFixed(2)}
              </Text>
              <Text fontSize="2xs" color="whiteAlpha.500">
                @ ${price.toFixed(2)}
              </Text>
            </VStack>
          </HStack>
        </Box>

        {/* EXPANDABLE DETAILS - Mobile gets collapse, Desktop always shows */}
        {isMobile ? (
          <>
            <Button
              size="xs"
              variant="ghost"
              w="full"
              color="whiteAlpha.600"
              rightIcon={showDetails ? <ChevronUpIcon /> : <ChevronDownIcon />}
              onClick={() => setShowDetails(!showDetails)}
              _hover={{ bg: "whiteAlpha.100" }}
            >
              {showDetails ? 'Hide Details' : 'Show Details'}
            </Button>

            <Collapse in={showDetails} animateOpacity style={{ width: '100%' }}>
              <VStack spacing={2} w="full">
                {/* QR Code */}
                {qrPath && (
                  <Image
                    src={qrPath}
                    boxSize="90px"
                    bg="white"
                    p={2}
                    borderRadius="lg"
                    boxShadow={`0 0 20px ${color.shadow}`}
                    border="2px solid"
                    borderColor="whiteAlpha.200"
                  />
                )}

                {/* Address */}
                <Tooltip label={address} hasArrow bg="gray.700" color="white" fontSize="xs">
                  <Box
                    bg="whiteAlpha.100"
                    px={2}
                    py={1.5}
                    borderRadius="md"
                    border="1px solid"
                    borderColor="whiteAlpha.200"
                    w="full"
                    textAlign="center"
                  >
                    <Text fontFamily="monospace" fontSize="xs" color="whiteAlpha.800">
                      {truncate(address)}
                    </Text>
                  </Box>
                </Tooltip>

                {/* Copy Button */}
                <Button
                  w="full"
                  size="sm"
                  bgGradient={color.gradient}
                  color="white"
                  fontWeight="bold"
                  leftIcon={copied ? <CheckIcon /> : <CopyIcon />}
                  onClick={copy}
                  isDisabled={copied}
                >
                  {copied ? "COPIED!" : "COPY"}
                </Button>

                {/* Explorer Link */}
                {explorer !== "#" && (
                  <Button
                    w="full"
                    size="xs"
                    color={color.color}
                    variant="outline"
                    borderColor={color.color}
                    as={Link}
                    href={explorer}
                    isExternal
                    rightIcon={<ExternalLinkIcon />}
                  >
                    EXPLORER
                  </Button>
                )}
              </VStack>
            </Collapse>
          </>
        ) : (
          // DESKTOP - Full details always visible
          <VStack spacing={3} w="full">
            {/* QR Code */}
            {qrPath && (
              <Box
                opacity={balanceRevealed ? 1 : 0}
                transform={balanceRevealed ? "scale(1)" : "scale(0.9)"}
                transition="all 0.5s cubic-bezier(0.34, 1.56, 0.64, 1) 0.2s"
              >
                <Image
                  src={qrPath}
                  boxSize="110px"
                  bg="white"
                  p={2}
                  borderRadius="lg"
                  boxShadow={`0 0 25px ${color.shadow}`}
                  border="2px solid"
                  borderColor="whiteAlpha.200"
                />
              </Box>
            )}

            {/* Address */}
            <Tooltip label={address} hasArrow bg="gray.700" color="white" fontSize="xs">
              <Box
                bg="whiteAlpha.100"
                px={3}
                py={2}
                borderRadius="lg"
                border="1px solid"
                borderColor="whiteAlpha.200"
                w="full"
                textAlign="center"
                backdropFilter="blur(10px)"
                cursor="pointer"
                _hover={{ bg: "whiteAlpha.150", borderColor: "whiteAlpha.300" }}
              >
                <Text fontFamily="monospace" fontSize="sm" color="whiteAlpha.800" fontWeight="semibold">
                  {truncate(address)}
                </Text>
              </Box>
            </Tooltip>

            {/* Copy Button */}
            <Button
              w="full"
              bgGradient={color.gradient}
              color="white"
              fontWeight="bold"
              size="lg"
              leftIcon={copied ? <CheckIcon /> : <CopyIcon />}
              onClick={copy}
              borderRadius="xl"
              boxShadow={`0 4px 15px ${color.shadow}`}
              _hover={{
                transform: "translateY(-2px)",
                boxShadow: `0 6px 20px ${color.shadow}`,
              }}
              isDisabled={copied}
            >
              {copied ? "COPIED!" : "COPY ADDRESS"}
            </Button>

            {/* Explorer Link */}
            {explorer !== "#" && (
              <Button
                w="full"
                color={color.color}
                variant="outline"
                borderColor={color.color}
                size="md"
                as={Link}
                href={explorer}
                isExternal
                rightIcon={<ExternalLinkIcon />}
                borderRadius="xl"
                _hover={{ bg: `${color.color}22`, transform: "translateY(-2px)" }}
              >
                VIEW ON EXPLORER
              </Button>
            )}
          </VStack>
        )}
      </VStack>
    </Box>
  );
}